import React, { useState, useEffect, useRef, useCallback } from "react";

// Modules
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme } from "@mui/material/styles";
import logo from "../../../assets/ca-logo.png";

import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  useSortable,
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import {
  restrictToVerticalAxis,
  restrictToWindowEdges,
} from "@dnd-kit/modifiers";
import { CSS } from "@dnd-kit/utilities";

// App
import AddSlide from "./slide/addSlide";
import AddHotelSlide from "./slide/addHotelSlide";
import AddLibrarySlide from "./slide/addLibrarySlide";
import AddTranslatedSlide from "./slide/addTranslatedSlide";
import Thumbnail from "./thumbnail";
import { jsonParser, getThemeJson } from "../../../utils/helpers";
import BlockTree from "./slide/edit/blockTree";
import SlideManagement from "./slideManagement";

// UI components
import { Row, Col } from "react-bootstrap";
import {
  Typography,
  Snackbar,
  Stack,
  Button,
  Checkbox,
  FormControl,
  Select,
  MenuItem,
  OutlinedInput,
  TextField,
  InputLabel,
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Fab,
  Tooltip,
  Alert,
  IconButton,
} from "@mui/material";
import { ReactComponent as NounLink } from "../../../assets/noun-link.svg";

import {
  DragIndicator,
  Add,
  TextSnippet,
  ContentCopy,
  InsertLink,
  Language,
  ChevronRight,
  DragHandle,
  Public,
} from "@mui/icons-material";
import DialogPaper from "../../../partials/dialogPaper";
import { client } from "../../../config";
import AlertModal from "../../../partials/alertModal";

// Adding a slide makes the parent null `presentation` during the reload, which
// unmounts and remounts this whole Sidebar — wiping any instance refs/state.
// Persist the "re-open slide management after the reload" intent at module
// scope so it survives that remount. Consumed (reset) as soon as it's honored.
let pendingReopenAfterReload = false;

export default function Sidebar(props) {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down("md"));

  // The dynamic hotel slide is gated three ways:
  //
  //  - the `access dynamic hotel slide` Drupal permission, so who gets it is a
  //    role decision on the CMS rather than something baked into this build;
  //  - the Radisson client, whose portfolio API it is built on;
  //  - the presence of the `dynamic-at-a-glance` layout, which currently ships
  //    only in the `rhg` theme. Testing for the layout rather than the theme
  //    name means the option appears by itself if the layout is ever rolled out
  //    to radisson_core, park_plaza etc., and stays hidden on decks whose theme
  //    cannot render it (where addHotelSlides() would otherwise fail on a null
  //    layout).
  const hotelSlideLayouts = new Set(
    (getThemeJson(props.presentation?.theme?.label)?.layouts || []).map(
      (l) => l.id,
    ),
  );
  const hotelSlideEnabled =
    Boolean(props.permissions?.access_dynamic_hotel_slide) &&
    client === "radisson" &&
    hotelSlideLayouts.has("dynamic-at-a-glance");
  // The picker's "Meetings" button creates the M&E variant, which is a separate
  // layout and so a separate lookup — the standard layout shipping in a theme
  // is no guarantee the M&E one did.
  const hotelSlideMeEnabled = hotelSlideLayouts.has("dynamic-at-a-glance-me");
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  const presentationJson = jsonParser(props?.presentation.json);
  const selectedAction = "";

  const [fromLayoutOpen, setFromLayoutOpen] = useState(false);
  const [fromHotelLibraryOpen, setFromHotelLibraryOpen] = useState(false);
  const [fromLibraryOpen, setFromLibraryOpen] = useState(false);
  const [fromCloneOpen, setFromCloneOpen] = useState(false);
  const [fromTranslateOpen, setFromTranslateOpen] = useState(false);
  const [showMoveSlides, setShowMoveSlides] = useState(false);
  const [showDuplicateSlides, setShowDuplicateSlides] = useState(false);
  const [numberOfDuplicates, setNumberOfDuplicates] = useState(1);
  const [showSlideDuplicateError, setSlideDuplicateError] = useState(false);
  const [slidesPosition, setSlidesPosition] = useState("");
  const [moveTarget, setMoveTarget] = useState(null);

  const [showPublishSlides, setShowPublishSlides] = useState(false);
  // Library ("COPY from library") slides can't be thumbnailed/published here;
  // holds the "some of your selection was excluded" dialog payload.
  const [libraryExcludeDialog, setLibraryExcludeDialog] = useState(null);

  const [hasChanges, setHasChange] = useState(false);
  const [slideAddIndex, setSlideAddIndex] = useState(0);
  const [newSlideDialog, setNewSlideDialogOpen] = useState(false);
  const [slides, setSlides] = useState(props?.presentation?.slides);
  const [selectedSlides, setSelectedSlides] = useState([]);

  const [scrollToItemId, setScrollToItemId] = useState(null);
  const scrollToItemRef = useRef(null);

  const [open, setOpen] = useState(false);
  const [slideManagementOpen, setSlideManagementOpen] = useState(false);
  // When the add-slide flow is launched from inside slide management, remember
  // to re-open the manager once the flow finishes (added) or is cancelled.
  const reopenManagerRef = useRef(false);

  // Listen to changes for slides
  useEffect(() => {
    let original_order = [];
    let new_order = [];

    for (let i = 0; i < props?.presentation?.slides.length; i++) {
      original_order.push(props?.presentation?.slides[i].id);
    }

    for (let i = 0; i < slides.length; i++) {
      new_order.push(slides[i].id);
    }

    const x = original_order.toString();
    const y = new_order.toString();

    if (x !== y) {
      setHasChange(true);
    } else {
      setHasChange(false);
    }
  }, [slides, props?.presentation?.slides]);

  // Re-render on slide change
  useEffect(() => {
    setSlides(props?.presentation?.slides);
  }, [props?.presentation?.slides]);

  // After an add-from-manager, the parent reloads the presentation (nulling it,
  // which remounts this Sidebar). This effect runs on the fresh mount once the
  // refreshed slides land — re-open slide management so the new slide is shown.
  useEffect(() => {
    if (pendingReopenAfterReload) {
      pendingReopenAfterReload = false;
      setSlideManagementOpen(true);
    }
  }, [props?.presentation?.slides]);

  // Warn before leaving with unsaved slide-order changes. React Router 6 removed
  // navigator.block, and this app mounts a BrowserRouter (not a data router), so
  // the native useBlocker hook isn't available either — guard browser-level
  // navigation (refresh / tab close / back) via beforeunload instead.
  useEffect(() => {
    if (!hasChanges) return;

    const handleBeforeUnload = (e) => {
      e.preventDefault();
      e.returnValue = "";
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [hasChanges]);

  const handleNewSlideDialogClickOpen = (index) => {
    setNewSlideDialogOpen(true);
    setSlideAddIndex(index);
  };

  const handleNewSlideDialogClose = () => {
    setNewSlideDialogOpen(false);
  };

  // A slide was added from the manager: a reload follows, so don't re-open
  // immediately — flag it and let the reload-watching effect re-open once the
  // refreshed slides arrive.
  const returnToManagerAfterAdd = () => {
    if (reopenManagerRef.current) {
      reopenManagerRef.current = false;
      pendingReopenAfterReload = true;
    }
  };

  // Nothing was added (flow cancelled), so there's no reload to wait for —
  // re-open the manager right away.
  const returnToManagerAfterCancel = () => {
    if (reopenManagerRef.current) {
      reopenManagerRef.current = false;
      setSlideManagementOpen(true);
    }
  };

  // ─── Stable handlers for <AddHotelSlide> ─────────────────────────────────
  // That modal is React.memo'd, which only buys anything if its props keep
  // their identity. Everything its handlers need -- `slideAddIndex`, the
  // `returnToManager*` helpers, `props.onAddHotelSlides` (an inline arrow in
  // EditPresentation, so new every render up there) -- is re-created on each
  // render of this component, so the handlers read through a ref that is kept
  // current instead of closing over any of it. That lets the useCallbacks below
  // have empty dependency lists and genuinely never change.
  const hotelSlideHandlers = useRef();
  hotelSlideHandlers.current = {
    slideAddIndex,
    returnToManagerAfterAdd,
    returnToManagerAfterCancel,
    onAddHotelSlides: props?.onAddHotelSlides,
  };

  const closeHotelLibrary = useCallback(() => {
    setFromHotelLibraryOpen(false);
    hotelSlideHandlers.current.returnToManagerAfterCancel();
  }, []);

  // `entries` is the picker's `{ hotel, variant }` list, one per slide.
  const addHotelSlidesFromLibrary = useCallback((entries, onProgress) => {
    const h = hotelSlideHandlers.current;
    return Promise.resolve(
      h.onAddHotelSlides(entries, h.slideAddIndex, onProgress),
    ).then(() => {
      setNewSlideDialogOpen(false);
      setFromHotelLibraryOpen(false);
      h.returnToManagerAfterAdd();
    });
  }, []);

  // Cancelling the new-slide picker (as opposed to choosing an option, which
  // transitions to a sub-dialog) ends the flow, so return to the manager.
  const handleNewSlideDialogCancel = () => {
    handleNewSlideDialogClose();
    returnToManagerAfterCancel();
  };

  const handleShowMoveSlideClose = () => {
    setShowMoveSlides(false);
    setMoveTarget(null);
  };

  const handleShowDuplicateSlideClose = () => {
    setShowDuplicateSlides(false);
    setSlideDuplicateError(false);
  };

  const handleShowPublishSlidesClose = () => {
    setShowPublishSlides(false);
  };

  // A library ("COPY from library") slide is linked to an original in another
  // presentation, so its content and thumbnail live with that original — it
  // can't be thumbnailed or published from here. Split a selection into the
  // slides we can act on vs the excluded library slides (by 1-based number).
  const splitByLibrary = (ids) => {
    const regular = [];
    const libraryIndices = [];
    (ids || []).forEach((id) => {
      const index = slides.findIndex((s) => s.id === id);
      if (index === -1) return;
      if (slides[index].initial_presentation !== props.presentation.id) {
        libraryIndices.push(index + 1);
      } else {
        regular.push(id);
      }
    });
    return { regular, libraryIndices };
  };

  const handlePublishSlides = () => {
    // Library slides are excluded from partial publish (the dialog note tells
    // the user); publish only the regular slides.
    const { regular } = splitByLibrary(selectedSlides);
    props.onPublishSlides(regular);
    setSelectedSlides([]);
    handleShowPublishSlidesClose();
  };

  // Message body for the library-exclusion warning (matches the wording style
  // of the existing library prompts, e.g. the theme-exclude AlertModal).
  const libraryExcludeMessage = () => {
    const d = libraryExcludeDialog;
    if (!d) return "";
    const nums = d.libraryIndices.join(", ");
    const plural = d.libraryIndices.length > 1;
    const lead = plural
      ? `Slides ${nums} are 'COPY' slides from the library. Their thumbnails come from the original presentation, so they can't be generated here and have been excluded from your selection.`
      : `Slide ${nums} is a 'COPY' slide from the library. Its thumbnail comes from the original presentation, so it can't be generated here and has been excluded from your selection.`;
    const tail =
      d.regular.length > 0
        ? " Continue generating thumbnails for the remaining slide(s)?"
        : " There are no other slides in your selection.";
    return `${lead}${tail}`;
  };

  // "Continue" from the library-exclusion dialog: run the action on the
  // remaining (regular) slides only.
  const handleLibraryExcludeContinue = () => {
    if (!libraryExcludeDialog) return;
    const { action, regular } = libraryExcludeDialog;
    if (action === "thumbnail" && regular.length > 0) {
      props.generateThumbnails(regular);
      setOpen(true);
    }
    setSelectedSlides([]);
    setLibraryExcludeDialog(null);
  };

  const handleActionChange = (event) => {
    if (selectedSlides.length > 0) {
      if (event.target.value === "delete") {
        props.onRemoveSlides(selectedSlides);
        setSelectedSlides([]);
      }

      if (event.target.value === "draft") {
        props.toggleStatus(selectedSlides);
        setSelectedSlides([]);
      }

      if (event.target.value === "move") {
        setShowMoveSlides(true);
      }

      if (event.target.value === "thumbnail") {
        const { regular, libraryIndices } = splitByLibrary(selectedSlides);
        if (libraryIndices.length > 0) {
          // Some/all selected are library slides — show the exclusion dialog
          // and continue with the regular slides only.
          setLibraryExcludeDialog({
            action: "thumbnail",
            regular,
            libraryIndices,
          });
        } else {
          props.generateThumbnails(selectedSlides);
          setOpen(true);
          setSelectedSlides([]);
        }
      }

      if (event.target.value === "publish") {
        setShowPublishSlides(true);
      }

      if (event.target.value === "duplicate") {
        const slide = slides.find((s) => s.id === selectedSlides[0]);
        const slideJson = jsonParser(slide.slide_json);
        const isLibrary = slide.initial_presentation !== props.presentation.id;

        // prevent on "Disable Duplicate", Library slide and Advanced

        if (
          slide.prevent_duplicate === "1" ||
          slideJson?.reveal?.slideLayout?.id === "advanced" ||
          isLibrary
        ) {
          setSlideDuplicateError(true);
        }

        setShowDuplicateSlides(true);
      }
    }
  };

  const handleMoveSlides = () => {
    let targetPosition;
    if (moveTarget === "start") {
      targetPosition = 0;
    } else if (moveTarget === "end") {
      targetPosition = slides.length;
    } else {
      targetPosition = parseInt(slidesPosition, 10);
    }
    if (Number.isNaN(targetPosition) || targetPosition < 0) {
      handleShowMoveSlideClose();
      return;
    }
    const clampedTarget = Math.min(slides.length, targetPosition);

    let selected_slides_excluded = [];
    // Selected slides among the first `clampedTarget` of the original list —
    // their removal shifts the "after slide N" insertion index left by this many.
    let selectedBeforeTarget = 0;
    // Collect selected slides in original sidebar order, not click order, so
    // their relative ordering is preserved after the move.
    const ordered_selected_slides = [];

    slides.forEach((slide, idx) => {
      const isSelected = selectedSlides.indexOf(slide.id) !== -1;
      if (!isSelected) {
        selected_slides_excluded.push(slide.id);
      } else {
        ordered_selected_slides.push(slide.id);
        if (idx < clampedTarget) {
          selectedBeforeTarget++;
        }
      }
    });

    const position = Math.max(0, clampedTarget - selectedBeforeTarget);

    let new_slides = [...selected_slides_excluded];
    new_slides.splice(position, 0, ...ordered_selected_slides);

    const activeSlideId = props.presentation?.slides?.[props.indexh]?.id;
    const newActiveIndex =
      activeSlideId != null ? new_slides.indexOf(activeSlideId) : -1;

    props?.reorderSlidesQuietly &&
      props.reorderSlidesQuietly(
        new_slides,
        newActiveIndex >= 0 ? newActiveIndex : position,
      );

    setSelectedSlides([]);
    handleShowMoveSlideClose();
  };

  const processNotes = () => {
    let notesObj = {};
    if (selectedSlides.length > 0) {
      selectedSlides.forEach((e) => {
        if (presentationJson?.notes?.hasOwnProperty(e)) {
          notesObj[e] = presentationJson.notes[e];
        }
      });
    }
    if (Object.keys(notesObj).length > 0) {
      return notesObj;
    } else return null;
  };

  const processAudio = () => {
    let slideAudio = {};
    if (selectedSlides.length > 0) {
      selectedSlides.forEach((e) => {
        if (presentationJson?.slideAudio?.hasOwnProperty(e)) {
          slideAudio[e] = presentationJson.slideAudio[e];
        }
      });
    }
    if (Object.keys(slideAudio).length > 0) {
      return slideAudio;
    } else return null;
  };

  const handleDuplicateSlides = () => {
    props.onDuplicateSlides(
      selectedSlides,
      numberOfDuplicates,
      processNotes(),
      processAudio(),
    );
    setSelectedSlides([]);
    setNumberOfDuplicates(1);
    handleShowDuplicateSlideClose();
    setSlideDuplicateError(false);
  };

  // DnD callback
  function handleDragEnd(event) {
    const { active, over } = event;

    if (active.id !== over.id) {
      setSlides((slides) => {
        const oldIndex = slides.findIndex((f) => f.id === active.id);
        const newIndex = slides.findIndex((f) => f.id === over.id);
        return arrayMove(slides, oldIndex, newIndex);
      });
    }
  }

  // Scroll to specific item
  useEffect(() => {
    if (scrollToItemId && scrollToItemRef && scrollToItemRef.current) {
      setTimeout(() => {
        scrollToItemRef.current.scrollIntoView({
          behavior: "smooth",
          block: "center",
        });
      }, 250);
    }
  }, [scrollToItemId]);

  // Function to scroll to a specific SortableItem
  const scrollToItem = (itemId) => {
    setScrollToItemId(itemId);
  };

  useEffect(() => {
    const slide = slides[props.indexh];

    if (slide && slide.id) {
      scrollToItem(slide.id);
    }
  }, [props.indexh, slides]);

  // Library slides are excluded from partial publish; surface which ones and
  // disable publishing when nothing publishable remains in the selection.
  const publishSplit = splitByLibrary(selectedSlides);
  // When the selection is ONLY library slides, disable (grey out) the actions
  // that can't operate on them (thumbnail, publish, hide, duplicate); Move and
  // Delete stay enabled. Mixed selections keep every action enabled (they run
  // on the regular slides, excluding the library ones at run time).
  const selectionAllLibrary =
    selectedSlides.length > 0 && publishSplit.regular.length === 0;

  return (
    <div className={`sidebar-container ${hasChanges ? "overflow" : ""}`}>
      <div className="sidebar-management-host">
        <div className="sidebar-management-control">
          <Tooltip
            title="Drag to resize · click to collapse"
            placement="top"
            enterDelay={400}
          >
            <IconButton
              className="sidebar-management-control__drag ps-sidebar-resize-container"
              aria-label="Resize sidebar"
              size="small"
              tabIndex={-1}
            >
              <DragHandle />
            </IconButton>
          </Tooltip>
          <Tooltip
            title="Open slide management"
            placement="top"
            enterDelay={400}
          >
            <IconButton
              className="sidebar-management-control__chevron"
              aria-label="Open slide management"
              size="small"
              onClick={() => setSlideManagementOpen(true)}
            >
              <ChevronRight />
            </IconButton>
          </Tooltip>
        </div>
      </div>

      <SlideManagement
        open={slideManagementOpen}
        onClose={() => setSlideManagementOpen(false)}
        presentation={props.presentation}
        indexh={props.indexh}
        navigateTo={props.navigateTo}
        thumbnailVersions={props.thumbnailVersions}
        onRequestAddSlide={(index) => {
          // Close slide management and reuse the sidebar's existing
          // new-slide dialog (with its layout/library/clone/translate
          // options) at the requested insert position. Flag so the manager
          // re-opens once the add flow completes or is cancelled.
          reopenManagerRef.current = true;
          setSlideManagementOpen(false);
          handleNewSlideDialogClickOpen(index);
        }}
        reorderSlidesQuietly={(newOrderIds, newActiveIndex) =>
          props.reorderSlidesQuietly &&
          props.reorderSlidesQuietly(newOrderIds, newActiveIndex)
        }
        onRemoveSlides={(_slides) => props.onRemoveSlides(_slides)}
        toggleStatus={(_slides) => props.toggleStatus(_slides)}
        generateThumbnails={(_slides) => props.generateThumbnails(_slides)}
        onRenameSlide={(slideId, title) =>
          props.onRenameSlide && props.onRenameSlide(slideId, title)
        }
        onPublishSlides={(_slides) =>
          props.onPublishSlides && props.onPublishSlides(_slides)
        }
        onDuplicateSlides={(_slides, count, notes, slideAudio) =>
          props.onDuplicateSlides &&
          props.onDuplicateSlides(_slides, count, notes, slideAudio)
        }
      />

      <Box className="actions">
        <FormControl fullWidth size="small">
          <InputLabel
            sx={{ fontSize: 14 }}
            size="small"
            id="slide-actions-label"
          >
            Actions
          </InputLabel>
          <Select
            labelId="slide-label"
            id="slide-actions-select"
            value={selectedAction}
            label="Actions"
            disabled={
              selectedSlides.length > 0 && !props.isPublishing ? false : true
            }
            variant={"outlined"}
            onChange={(event) => {
              handleActionChange(event);
            }}
            input={<OutlinedInput sx={{ fontSize: 14 }} label="Actions" />}
          >
            <MenuItem
              value={"thumbnail"}
              sx={{ fontSize: 14 }}
              disabled={selectionAllLibrary}
            >
              Generate Thumbnail
            </MenuItem>
            <MenuItem
              value={"publish"}
              sx={{ fontSize: 14 }}
              disabled={selectionAllLibrary}
            >
              Publish Slide(s)
            </MenuItem>
            <MenuItem
              value={"draft"}
              sx={{ fontSize: 14 }}
              disabled={selectionAllLibrary}
            >
              Toggle Hide
            </MenuItem>
            <MenuItem value={"move"} sx={{ fontSize: 14 }}>
              Move Slide(s)
            </MenuItem>
            <MenuItem
              value={"duplicate"}
              sx={{ fontSize: 14 }}
              disabled={selectionAllLibrary || selectedSlides.length > 1}
            >
              <Tooltip
                id="duplicate-slide"
                title="You can only duplicate one slide at a time and a maximum of 10 duplicates."
                placement="right"
              >
                Duplicate Slide
              </Tooltip>
            </MenuItem>
            <MenuItem value={"delete"} sx={{ fontSize: 14 }}>
              Delete
            </MenuItem>
          </Select>
        </FormControl>
      </Box>

      {fromLayoutOpen && (
        <AddSlide
          title={"Slide layout"}
          fromLayoutOpen={fromLayoutOpen}
          onLayoutClose={() => {
            setFromLayoutOpen(false);
            returnToManagerAfterCancel();
          }}
          presentation={props?.presentation}
          samplePresentation={props?.samplePresentation}
          samplePresentationLoading={props?.samplePresentationLoading}
          permissions={props.permissions}
          user={props?.user}
          onAddSlide={(slide, layout) => {
            setNewSlideDialogOpen(false);
            props?.onAddSlide(slide, layout, slideAddIndex);
            setFromLayoutOpen(false);
            returnToManagerAfterAdd();
          }}
        />
      )}

      {hotelSlideEnabled && fromHotelLibraryOpen && (
        <AddHotelSlide
          open={fromHotelLibraryOpen}
          meEnabled={hotelSlideMeEnabled}
          onClose={closeHotelLibrary}
          onAddHotelSlides={addHotelSlidesFromLibrary}
        />
      )}

      {fromLibraryOpen && (
        <AddLibrarySlide
          title={"Presentation Library"}
          type="copy"
          fromLibraryOpen={fromLibraryOpen}
          onLayoutClose={() => {
            setFromLibraryOpen(false);
            returnToManagerAfterCancel();
          }}
          user={props?.user}
          presentation={props?.presentation}
          categories={props.categories}
          languages={props.languages}
          themes={props.themes}
          onAddSlides={(slides, notes, slideAudio) => {
            setNewSlideDialogOpen(false);
            props?.onAddLibrarySlides(slides, slideAddIndex, notes, slideAudio);
            setFromLibraryOpen(false);
            returnToManagerAfterAdd();
          }}
        />
      )}

      {fromCloneOpen && (
        <AddLibrarySlide
          title={"Presentation Library"}
          fromLibraryOpen={fromCloneOpen}
          onLayoutClose={() => {
            setFromCloneOpen(false);
            returnToManagerAfterCancel();
          }}
          user={props?.user}
          presentation={props?.presentation}
          categories={props.categories}
          languages={props.languages}
          themes={props.themes}
          type={"clone"}
          onAddSlides={(slides, notes, slideAudio) => {
            setNewSlideDialogOpen(false);
            props?.onAddCloneSlides(slides, slideAddIndex, notes, slideAudio);
            setFromCloneOpen(false);
            returnToManagerAfterAdd();
          }}
        />
      )}

      {fromTranslateOpen && (
        <AddTranslatedSlide
          title={"Add Translated Slide"}
          fromTranslateOpen={fromTranslateOpen}
          onLayoutClose={() => {
            setFromTranslateOpen(false);
            returnToManagerAfterCancel();
          }}
          user={props?.user}
          presentation={props?.presentation}
          categories={props.categories}
          languages={props.languages}
          themes={props.themes}
          onAddTranslatedSlides={(slides, _language, notes, slideAudio) => {

            setNewSlideDialogOpen(false);
            props?.onAddTranslatedSlides(
              slides,
              _language,
              slideAddIndex,
              notes,
              slideAudio,
            );
            setFromTranslateOpen(false);
            returnToManagerAfterAdd();
          }}
        />
      )}

      {slides.length > 0 ? (
        <div className={`thumbnail-wrapper slides`}>
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            // onDragStart={handleDragStart}
            onDragEnd={handleDragEnd}
            modifiers={[restrictToVerticalAxis, restrictToWindowEdges]}
          >
            <SortableContext
              handle
              items={slides.map((slide) => slide.id)}
              strategy={verticalListSortingStrategy}
            >
              {slides.map((slide, index) => {
                const isSelected =
                  selectedSlides.indexOf(slide.id) > -1 ? true : false;
                const isLibrarySlide =
                  slide.initial_presentation === props.presentation.id
                    ? false
                    : true;

                return (
                  <SortableItem
                    key={`sortable-${slide.id}`}
                    refProp={index === props.indexh ? scrollToItemRef : null}
                    index={index}
                    id={slide.id}
                    slide={slide}
                    indexh={props?.indexh}
                    navigateTo={props?.navigateTo}
                    originalSlides={props?.presentation?.slides}
                    handleNewSlideDialogClickOpen={(slideIndex) => {
                      let theIndex = slideIndex;
                      if (!theIndex || isNaN(theIndex)) {
                        theIndex = 0;
                      }
                      handleNewSlideDialogClickOpen(theIndex);
                    }}
                    handleNewSlideDialogClose={handleNewSlideDialogClose}
                    hasChanges={hasChanges}
                    onSlideToggled={() => {
                      if (selectedSlides.includes(slide.id)) {
                        const newArray = selectedSlides.filter(
                          (e) => e !== slide.id,
                        );
                        setSelectedSlides(newArray);
                      } else {
                        const newArray = [...selectedSlides, slide.id];
                        setSelectedSlides(newArray);
                      }
                    }}
                    isSelected={isSelected}
                    isDragDisabled={selectedSlides.length > 0 ? true : false}
                    isLibrarySlide={isLibrarySlide}
                    presentation={{ id: props.presentation.id }}
                    isFirst={index === 0 ? true : false}
                    isLast={index === slides.length - 1 ? true : false}
                    thumbnailVersion={props?.thumbnailVersions?.[slide.id]}
                  />
                );
              })}
            </SortableContext>
          </DndContext>
        </div>
      ) : (
        <div className={`thumbnail-wrapper slides`}>
          <Row className="mt-2 text-center">
            <Col>
              <Button
                startIcon={<Add />}
                onClick={() => {
                  handleNewSlideDialogClickOpen(0);
                }}
              >
                Add slide
              </Button>
            </Col>
          </Row>
        </div>
      )}

      {hasChanges && (
        <div className="sidebar-save">
          <Button
            variant="contained"
            onClick={() => {
              const new_order = slides.map((s) => s.id);
              const activeSlideId =
                props.presentation?.slides?.[props.indexh]?.id;
              const newActiveIndex =
                activeSlideId != null
                  ? new_order.indexOf(activeSlideId)
                  : props.indexh;

              props?.reorderSlidesQuietly &&
                props.reorderSlidesQuietly(
                  new_order,
                  newActiveIndex >= 0 ? newActiveIndex : props.indexh,
                );
            }}
          >
            Save
          </Button>
        </div>
      )}

      <BlockTree />

      <Dialog
        fullScreen={fullScreen}
        open={newSlideDialog}
        onClose={handleNewSlideDialogCancel}
        aria-labelledby="add-slide-dialog"
        maxWidth={"sm"}
        fullWidth={true}
        className="add-library-modal"
        PaperComponent={DialogPaper}
      >
        <DialogTitle
          id="add-slide-dialog-title"
          className="dialog-draggable-handle"
          sx={{
            backgroundColor: "primary.light",
          }}
        >
          <Row className="d-flex align-items-center">
            <Col>
              <Typography variant="h3" owner="admin" color="adminText.dark">
                {"Add slide"}
              </Typography>
            </Col>
            <Col className="d-flex justify-content-end">
              <Button
                variant="outlined"
                owner="admin"
                dialogbtn="true"
                dialogvariant="cancel"
                onClick={handleNewSlideDialogCancel}
              >
                <Typography variant="button" owner="admin" className="lh">
                  Cancel
                </Typography>
              </Button>
            </Col>
          </Row>
        </DialogTitle>
        <List sx={{ pt: 0 }}>
          <ListItem
            button
            onClick={() => {
              setFromLayoutOpen(true);
              handleNewSlideDialogClose();
            }}
          >
            <ListItemIcon
              sx={{
                color: "primary.main",
              }}
            >
              <TextSnippet />
            </ListItemIcon>
            <ListItemText
              sx={{
                color: "primary.main",
              }}
              primary="Create NEW from layout"
              secondary="Creates a new, original slide"
            />
          </ListItem>

          <ListItem
            button
            onClick={() => {
              setFromLibraryOpen(true);
              handleNewSlideDialogClose();
            }}
          >
            <ListItemIcon
              sx={{
                color: "primary.main",
              }}
            >
              <InsertLink />
            </ListItemIcon>
            <ListItemText
              sx={{
                color: "primary.main",
              }}
              primary={"Create COPY from library"}
              secondary={
                "A non-editable copy of a slide, which is linked to the original"
              }
            />
          </ListItem>

          <ListItem
            button
            onClick={() => {
              setFromCloneOpen(true);
              handleNewSlideDialogClose();
            }}
          >
            <ListItemIcon
              sx={{
                color: "primary.main",
              }}
            >
              <ContentCopy />
            </ListItemIcon>
            <ListItemText
              sx={{
                color: "primary.main",
              }}
              primary={"Create DUPLICATE from library"}
              secondary={
                "An editable copy of a slide, which in NOT linked to the original"
              }
            />
          </ListItem>

          {hotelSlideEnabled && (
            <ListItem
              button
              onClick={() => {
                setFromHotelLibraryOpen(true);
                handleNewSlideDialogClose();
              }}
            >
              <ListItemIcon sx={{ color: "primary.main" }}>
                <Public />
              </ListItemIcon>
              <ListItemText
                sx={{ color: "primary.main" }}
                primary={"Create DYNAMIC hotel slide from library"}
                secondary={
                  "A dynamically created slide for any hotel in the portfolio"
                }
              />
            </ListItem>
          )}

          {props.permissions.access_slide_translator && (
            <ListItem
              button
              onClick={() => {
                setFromTranslateOpen(true);
                handleNewSlideDialogClose();
              }}
            >
              <ListItemIcon
                sx={{
                  color: "primary.main",
                }}
              >
                <Language />
              </ListItemIcon>
              <ListItemText
                sx={{
                  color: "primary.main",
                }}
                primary="Create TRANSLATED version from library with AI"
                secondary="Creates a new, translated version of the original slide"
              />
            </ListItem>
          )}
        </List>
        <Box className="alert-container">
          <Alert severity="warning">
            PLEASE NOTE: DUPLICATE functionality is not available on all slides
          </Alert>
        </Box>
        <DialogActions></DialogActions>
      </Dialog>

      <Dialog
        fullScreen={fullScreen}
        open={showMoveSlides}
        onClose={handleShowMoveSlideClose}
        aria-labelledby="move-slide-dialog"
        id="move-slide-dialog"
        maxWidth={"sm"}
        fullWidth={true}
        PaperComponent={DialogPaper}
      >
        <DialogTitle className="d-flex justify-content-center align-items-center dialog-draggable-handle">
          <img className="ca-logo" src={logo} alt="ca-logo" />
          <Typography owner="admin" variant="h5" color="adminText.dark">
            Move Slide(s)
          </Typography>
        </DialogTitle>
        <DialogContent className="pt-3">
          <DialogContentText>
            <Typography
              owner="admin"
              variant="body1"
              color="adminText.main"
              className="pb-3"
            >
              Enter a slide number, or use one of the buttons below.
            </Typography>
          </DialogContentText>
          <Button
            owner="admin"
            variant={moveTarget === "start" ? "contained" : "outlined"}
            dialogbtn="true"
            dialogvariant="primary"
            className="ms-0 me-3"
            onClick={() =>
              setMoveTarget(moveTarget === "start" ? null : "start")
            }
            sx={{minWidth: 100}}
          >
            <Typography owner="admin" variant="button" className="lh">
              To start
            </Typography>
          </Button>
          <Button
            owner="admin"
            variant={moveTarget === "end" ? "contained" : "outlined"}
            dialogbtn="true"
            dialogvariant="primary"
            className="ms-0 me-0"
            onClick={() =>
              setMoveTarget(moveTarget === "end" ? null : "end")
            }
            sx={{minWidth: 100}}
          >
            <Typography owner="admin" variant="button" className="lh">
              To end
            </Typography>
          </Button>
          <TextField
            autoFocus
            margin="dense"
            id="slides-position"
            className="mt-4"
            label="Move after current slide"
            type="number"
            fullWidth
            variant="outlined"
            value={slidesPosition}
            InputLabelProps={{ shrink: true }}
            InputProps={{
              inputProps: {
                max: slides.length,
                min: 1,
              },
            }}
            onChange={(event) => {
              setSlidesPosition(event.target.value);
              setMoveTarget(null);
            }}
            onBlur={() => {
              if (slidesPosition > slides.length) {
                setSlidesPosition(slides.length);
              }
              if (slidesPosition !== "" && slidesPosition < 1) {
                setSlidesPosition(1);
              }
            }}
          />
        </DialogContent>
        <DialogActions className="pb-3 px-3 d-flex justify-content-center align-items-center">
          <Button
            variant="outlined"
            owner="admin"
            dialogbtn="true"
            dialogvariant="cancel"
            onClick={handleShowMoveSlideClose}
          >
            <Typography variant="button" owner="admin" className="lh">
              Cancel
            </Typography>
          </Button>
          <Button
            variant="contained"
            owner="admin"
            dialogbtn="true"
            dialogvariant="save"
            className="lh"
            onClick={handleMoveSlides}
          >
            <Typography variant="button" owner="admin" className="lh">
              Confirm
            </Typography>
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        fullScreen={fullScreen}
        open={showDuplicateSlides}
        onClose={handleShowDuplicateSlideClose}
        aria-labelledby="duplicate-slide-dialog"
        id="duplicate-slide-dialog"
        maxWidth={"sm"}
        fullWidth={true}
        PaperComponent={DialogPaper}
      >
        <DialogTitle className="d-flex justify-content-center align-items-center dialog-draggable-handle">
          <img className="ca-logo" src={logo} alt="ca-logo" />
          <Typography owner="admin" variant="h5" color="adminText.dark">
            Duplicate Slide
          </Typography>
        </DialogTitle>
        <DialogContent className="pt-3">
          {showSlideDuplicateError ? (
            <DialogContentText>
              <Typography
                owner="admin"
                variant="body1"
                color="adminText.main"
                className="pb-3"
                sx={{ lineHeight: 1.4 }}
              >
                You cannot duplicate this slide. The slide is either a copy
                slide from the library, has been marked as 'disable duplicate',
                or is an Advanced slide using HTML code.
              </Typography>
            </DialogContentText>
          ) : (
            <>
              <DialogContentText>
                <Typography
                  owner="admin"
                  variant="body1"
                  color="adminText.main"
                  className="pb-3"
                >
                  Enter the number of duplicates to make.
                </Typography>
              </DialogContentText>

              <TextField
                autoFocus
                margin="dense"
                id="number-of-duplicates"
                className="mt-4"
                label="Number of duplicates"
                type="number"
                fullWidth
                variant="outlined"
                value={numberOfDuplicates}
                InputProps={{
                  inputProps: {
                    max: 10,
                    min: 1,
                  },
                }}
                onChange={(event) => {
                  setNumberOfDuplicates(event.target.value);
                }}
              />
            </>
          )}
        </DialogContent>
        <DialogActions className="pb-3 px-3 d-flex justify-content-center align-items-center">
          <Button
            variant="outlined"
            owner="admin"
            dialogbtn="true"
            dialogvariant="cancel"
            onClick={handleShowDuplicateSlideClose}
          >
            <Typography variant="button" owner="admin" className="lh">
              Cancel
            </Typography>
          </Button>
          <Button
            variant="contained"
            owner="admin"
            dialogbtn="true"
            dialogvariant="save"
            className="lh"
            onClick={handleDuplicateSlides}
            disabled={showSlideDuplicateError}
          >
            <Typography variant="button" owner="admin" className="lh">
              Confirm
            </Typography>
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        fullScreen={fullScreen}
        open={showPublishSlides}
        onClose={handleShowPublishSlidesClose}
        aria-labelledby="publish-slides-dialog"
        id="publish-slides-dialog"
        maxWidth={"sm"}
        fullWidth={true}
        PaperComponent={DialogPaper}
      >
        <DialogTitle className="d-flex justify-content-center align-items-center dialog-draggable-handle">
          <img className="ca-logo" src={logo} alt="ca-logo" />
          <Typography owner="admin" variant="h5" color="adminText.dark">
            Publish Slide(s)
          </Typography>
        </DialogTitle>
        <DialogContent className="pt-3">
          <DialogContentText>
            <Typography
              owner="admin"
              variant="body1"
              color="adminText.main"
              className="pb-3"
            >
              Publish the selected slide(s) to the live presentation? This updates the published deck without re-publishing the entire presentation. The presentation is locked during this process.
            </Typography>
          </DialogContentText>
          <Box className="alert-container">
            <Alert severity="info">
              N.B. The presentation must already be published. To publish brand new content, use the full publish first.
            </Alert>
          </Box>
          {publishSplit.libraryIndices.length > 0 && (
            <Box className="alert-container mt-2">
              <Alert severity="warning">
                {publishSplit.libraryIndices.length > 1
                  ? `Slides ${publishSplit.libraryIndices.join(
                      ", ",
                    )} are 'COPY' slides from the library and can't be published here — they'll be excluded.`
                  : `Slide ${publishSplit.libraryIndices.join(
                      ", ",
                    )} is a 'COPY' slide from the library and can't be published here — it'll be excluded.`}
                {publishSplit.regular.length === 0
                  ? " There are no other slides to publish."
                  : ""}
              </Alert>
            </Box>
          )}
        </DialogContent>
        <DialogActions className="pb-3 px-3 d-flex justify-content-center align-items-center">
          <Button
            variant="outlined"
            owner="admin"
            dialogbtn="true"
            dialogvariant="cancel"
            onClick={handleShowPublishSlidesClose}
          >
            <Typography variant="button" owner="admin" className="lh">
              Cancel
            </Typography>
          </Button>
          <Button
            variant="contained"
            owner="admin"
            dialogbtn="true"
            dialogvariant="save"
            className="lh"
            onClick={handlePublishSlides}
            disabled={publishSplit.regular.length === 0}
          >
            <Typography variant="button" owner="admin" className="lh">
              Publish
            </Typography>
          </Button>
        </DialogActions>
      </Dialog>

      <AlertModal
        showAlert={Boolean(libraryExcludeDialog)}
        showAlertCallback={() => setLibraryExcludeDialog(null)}
        showCaLogo={true}
        alertMessageTitle={"Warning"}
        alertMessageBody={libraryExcludeMessage()}
        cancelButton={libraryExcludeDialog?.regular?.length > 0}
        cancelButtonLabel={"Cancel"}
        confirmButton={true}
        confirmButtonLabel={
          libraryExcludeDialog?.regular?.length > 0 ? "Continue" : "OK"
        }
        confirmButtonColor={"primary"}
        onConfirm={handleLibraryExcludeContinue}
      />

      <Snackbar
        open={open}
        autoHideDuration={5000}
        onClose={() => setOpen(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={() => setOpen(false)}
          severity="success"
          sx={{ width: "100%" }}
        >
          <Typography variant="body">
            Selected thumbnails will be generated shortly!
          </Typography>
        </Alert>
      </Snackbar>
    </div>
  );
}

function SortableItem(props) {
  const { attributes, listeners, setNodeRef, transform, transition } =
    useSortable({ id: props.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  /**
   * Identifier to check which item is active
   *
   * If item order is not changed, use reveal index
   * Else use original slide index
   */
  let indexCheck = props.index;
  let navigateIndex = props.index;

  if (props.hasChanges) {
    // indexCheck = originalIndex;
    // navigateIndex = originalIndex;
  }

  const isFirst = props.isFirst;
  const isLast = props.isLast;

  const InitialAdd = () => {
    if (isFirst) {
      return (
        <div className="slide-add top">
          <Fab
            id="select-layout"
            onClick={() => {
              props.handleNewSlideDialogClickOpen(0);
            }}
            size="small"
            color="primary"
            aria-label="Select layout"
          >
            <Add />
          </Fab>
        </div>
      );
    } else {
      return null;
    }
  };

  if (props.slide.published === true) {
    return (
      <>
        <div
          ref={setNodeRef}
          style={style}
          className={`drag-wrap ${indexCheck === props.indexh ? "active" : ""}`}
          data-id={props.id}
          data-first={isFirst}
          data-last={isLast}
        >
          <InitialAdd />
          <div ref={props.refProp} className={`slide-thumb-container d-flex`}>
            <div className="slide-thumb-index">
              <Stack
                direction="column"
                spacing={1}
                justifyContent="center"
                alignItems="center"
              >
                <Button
                  disabled={true}
                  variant="text"
                  className="index-btn"
                  {...listeners}
                  {...attributes}
                >
                  {props.index + 1}
                </Button>
                <Button
                  disabled={props.isDragDisabled}
                  variant="text"
                  className="move-btn"
                  {...listeners}
                  {...attributes}
                >
                  <DragIndicator color="#777" fontSize={"14px"} />
                </Button>
                <div className="selector">
                  <Checkbox
                    checked={props.isSelected}
                    sx={{ "& .MuiSvgIcon-root": { fontSize: 16 } }}
                    onChange={() => {
                      props.onSlideToggled();
                    }}
                  />
                </div>
              </Stack>
            </div>
            <Tooltip
              title={
                props.isLibrarySlide ? (
                  <Box sx={{ lineHeight: 1.6, whiteSpace: "nowrap" }}>
                    <Box sx={{ mb: 0.75 }}>
                      P={props.slide.initial_presentation}
                      {props.slide.initial_presentation_title
                        ? ` (${props.slide.initial_presentation_title})`
                        : ""}
                    </Box>
                    <Box>
                      S={props.slide.id}
                      {props.slide.title ? ` (${props.slide.title})` : ""}
                    </Box>
                  </Box>
                ) : (
                  `S=${props.slide.id} (${props.slide.title})`
                )
              }
              placement="right"
              componentsProps={{
                tooltip: { sx: { maxWidth: "none" } },
              }}
            >
              <div
                className="slide-thumb-wrapper"
                onClick={() => {
                  props.navigateTo(navigateIndex);
                }}
              >
                <div className="slide-preview">
                  {props.isLibrarySlide && (
                    <div className="library-slide-indicator">
                      <span className="icon-container">
                        <span className="icon">
                          <NounLink
                            style={{
                              transform: "rotate(45deg)",
                              pointerEvents: "none",
                            }}
                          />
                        </span>
                        <span className="tri" />
                      </span>
                    </div>
                  )}

                  <Thumbnail
                    isSlide={true}
                    isActive={indexCheck === props.indexh}
                    slide={props.slide}
                    presentation={props?.presentation}
                    version={props.thumbnailVersion}
                  />
                </div>
              </div>
            </Tooltip>
          </div>

          <div className="slide-add">
            <Fab
              id="select-layout"
              onClick={() => {
                /**
                 * +1 to the index because that is the position where we will insert the slide to
                 */
                props.handleNewSlideDialogClickOpen(navigateIndex + 1);
              }}
              size="small"
              color="primary"
              aria-label="Select layout"
            >
              <Add />
            </Fab>
          </div>
        </div>
      </>
    );
  } else {
    return (
      <>
        <div
          ref={setNodeRef}
          style={style}
          className={`drag-wrap draft ${
            indexCheck === props.indexh ? "active" : ""
          }`}
          data-id={props.id}
          data-first={isFirst}
          data-last={isLast}
        >
          <InitialAdd />
          <div ref={props.refProp} className="slide-draft d-flex">
            <div className="slide-thumb-index">
              <Stack
                direction="column"
                spacing={1}
                justifyContent="center"
                alignItems="center"
              >
                <div className="selector">
                  <Checkbox
                    checked={props.isSelected}
                    sx={{ "& .MuiSvgIcon-root": { fontSize: 16 } }}
                    onChange={() => {
                      props.onSlideToggled();
                    }}
                  />
                </div>
              </Stack>
            </div>

            <div
              className="slide-thumb-draft d-flex align-items-center"
              onClick={() => {
                props.navigateTo(navigateIndex);
              }}
            >
              <div className="info">
                <span>{props.index + 1}</span>
                <span>{` (Hidden)`}</span>
              </div>
            </div>
          </div>

          <div className="slide-add">
            <Fab
              id="select-layout"
              onClick={() => {
                /**
                 * +1 to the index because that is the position where we will insert the slide to
                 */
                props.handleNewSlideDialogClickOpen(navigateIndex + 1);
              }}
              size="small"
              color="primary"
              aria-label="Select layout"
            >
              <Add />
            </Fab>
          </div>
        </div>
      </>
    );
  }
}
